import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { DataCardShell } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatRetreatDateRange,
  setupStatusLabel,
  statusChip,
} from "../../lifecycle/lifecycleUi";
import { retreatDisplayTitle, retreatLocationLine } from "../manageRetreatTypes";
import { SectionActionButtons } from "../SectionActionButtons";
import { dashboardContainedButtonSx } from "../dashboardUi";

export function SetupRetreatCard({
  r,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
  onSetupMail,
}: {
  r: LifecycleRetreat;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
  onSetupMail: (r: LifecycleRetreat) => void;
}) {
  const navigate = useNavigate();
  const percent = r.readinessPercent || 0;
  const status = setupStatusLabel(r);

  return (
    <DataCardShell
      title={retreatDisplayTitle(r)}
      subtitle={retreatLocationLine(r) || undefined}
      chips={statusChip(status)}
      fields={[
        {
          label: "Dates",
          value: formatRetreatDateRange(r.dateFrom, r.dateTo),
        },
        { label: "Language", value: formatLanguage(r.language) },
        {
          label: "Lead Couple",
          value: r.leadCoupleName || "Not assigned",
        },
        {
          label: "Publication Readiness",
          value: (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={700}>
                {percent}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ height: 8, borderRadius: 10 }}
              />
            </Stack>
          ),
        },
      ]}
      primaryAction={
        <Button
          size="small"
          variant="contained"
          onClick={() => navigate(`/retreat-setup/${r.id}`)}
          sx={{ width: { xs: "100%", sm: "auto" }, ...dashboardContainedButtonSx }}
        >
          Open Setup
        </Button>
      }
      secondaryActions={
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <SectionActionButtons
            r={r}
            kind="inSetup"
            isSuperAdmin={isSuperAdmin}
            onHistory={onHistory}
            onDelete={onDelete}
            onApprove={onApprove}
            onRestore={onRestore}
          />
          <Tooltip
            title={
              isSuperAdmin
                ? "Send Email to Lead Couples and Steering Committee"
                : "Send Email to Admin"
            }
          >
            <span
              onClick={() => onSetupMail(r)}
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSetupMail(r);
              }}
            >
              <i
                className="mdi mdi-email-outline font-size action-icons"
                title="Send Email"
                style={{ fontSize: "20px" }}
              />
            </span>
          </Tooltip>
        </Stack>
      }
    />
  );
}
